import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { FileLock2, CalendarClock, Users, Mail, LogOut, ShieldCheck, Library, Star, MessageSquarePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const nav = isAdmin
    ? [
        { to: "/admin/clients", icon: Users, label: "Clients" },
        { to: "/admin/library", icon: Library, label: "Library" },
        { to: "/admin/enquiries", icon: Mail, label: "Enquiries" },
        { to: "/admin/feedback", icon: Star, label: "Feedback" },
      ]
    : [
        { to: "/portal/documents", icon: FileLock2, label: "Documents" },
        { to: "/portal/meetings", icon: CalendarClock, label: "Meetings" },
        { to: "/portal/shared", icon: Library, label: "Shared" },
        { to: "/portal/feedback", icon: MessageSquarePlus, label: "Feedback" },
      ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <aside className="hidden md:flex flex-col w-64 border-r border-white/10 bg-[#080808] p-6 fixed inset-y-0">
        <div onClick={() => navigate("/")} className="cursor-pointer"><Logo size="sm" /></div>
        <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500 pl-1">
          <ShieldCheck size={13} /> {isAdmin ? "Administrator" : "Secure client area"}
        </div>
        <nav className="mt-10 flex flex-col gap-1 flex-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`portal-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all ${isActive ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
              <n.icon size={18} strokeWidth={1.5} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 pt-4">
          <div className="text-sm text-white truncate">{user?.full_name}</div>
          <div className="text-xs text-zinc-500 truncate mb-3">{user?.email}</div>
          <button onClick={logout} data-testid="portal-logout" className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#080808] border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <Logo size="sm" />
        <button onClick={logout} data-testid="portal-logout-mobile" className="text-zinc-400"><LogOut size={20} /></button>
      </div>

      <main className="flex-1 md:ml-64 pt-20 md:pt-0">
        <div className="md:hidden fixed top-14 inset-x-0 z-30 bg-[#0b0b0b] border-b border-white/10 flex">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `flex-1 text-center py-3 text-xs ${isActive ? "text-white border-b-2 border-white" : "text-zinc-500"}`}>
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 md:py-14 mt-10 md:mt-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
